import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import {
  Typography,
  Box,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material';
import { getUserMachines, MachineInfo, updateMachineName, deleteMachine } from '../../services/api';
import { SOCKET_URL } from '../../config/api';
import { toast } from 'react-toastify';
import { useI18n } from '../../i18n/useI18n';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { isStaffUser } from '../../utils/staffUser';
import { getAppProductChipProps } from '../../utils/deviceMgmtFeatures';
import UserPageShell, { userCardEnterSx } from '../shared/UserPageShell';
import {
  Computer as ComputerIcon,
  PhoneAndroid as PhoneAndroidIcon,
  ArrowForward as ArrowForwardIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Palette as PaletteIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import AppAccentColorApps from '../shared/AppAccentColorApps';

const MobileUploadMachineList: React.FC = () => {
  const { pages: t, formatMessage } = useI18n();
  const { user } = useSelector((state: RootState) => state.auth);
  const readOnly = isStaffUser(user);
  const navigate = useNavigate();
  const [machines, setMachines] = useState<MachineInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingMachine, setEditingMachine] = useState<MachineInfo | null>(null);
  const [newMachineName, setNewMachineName] = useState('');
  const [updating, setUpdating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingMachine, setDeletingMachine] = useState<MachineInfo | null>(null);
  const [deleting, setDeleting] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    loadMachines();
  }, []);

  useEffect(() => {
    const socket = io(SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 8,
      reconnectionDelay: 1500,
      path: '/socket.io',
    });
    socketRef.current = socket;

    const joinUserRoom = () => {
      socket.emit('join-user-room');
    };
    socket.on('connect', joinUserRoom);

    socket.on('app-status-update', (data: {
      machineCode: string;
      isAppRunning: boolean;
      appPlatform?: 'windows' | 'android' | null;
      appProduct?: 'lite' | 'mono' | 'pro' | null;
    }) => {
      if (!data?.machineCode) return;
      setMachines((prev) =>
        prev.map((m) =>
          m.machineCode.toUpperCase() === data.machineCode.toUpperCase()
            ? {
                ...m,
                isAppRunning: data.isAppRunning,
                appPlatform: data.isAppRunning
                  ? (data.appPlatform ?? m.appPlatform)
                  : m.appPlatform,
                appProduct: data.isAppRunning
                  ? (data.appProduct ?? m.appProduct)
                  : m.appProduct,
              }
            : m
        )
      );
    });

    return () => {
      socket.emit('leave-user-room');
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  const loadMachines = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getUserMachines();
      if (response.success) {
        setMachines(response.machines);
      } else {
        setError('Không thể tải danh sách máy');
      }
    } catch (err: any) {
      console.error('Error loading machines:', err);
      setError(err.response?.data?.error || 'Không thể tải danh sách máy');
      toast.error(err.response?.data?.error || 'Không thể tải danh sách máy');
    } finally {
      setLoading(false);
    }
  };

  const handleMachineClick = (machineCode: string) => {
    navigate(`/mobile-upload/${machineCode}`);
  };

  const handleEditClick = (machine: MachineInfo, event: React.MouseEvent) => {
    event.stopPropagation(); // Ngăn click event bubble lên card
    setEditingMachine(machine);
    setNewMachineName(machine.machineName || '');
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingMachine(null);
    setNewMachineName('');
  };

  const handleDeleteClick = (machine: MachineInfo, event: React.MouseEvent) => {
    event.stopPropagation();
    setDeletingMachine(machine);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeletingMachine(null);
  };

  const handleDeleteMachine = async () => {
    if (!deletingMachine) return;
    try {
      setDeleting(true);
      const response = await deleteMachine(deletingMachine.machineCode);
      if (response.success) {
        setMachines((prev) =>
          prev.filter((m) => m.machineCode !== deletingMachine.machineCode)
        );
        toast.success(t.deviceMgmt.deleteSuccess);
        handleCloseDeleteDialog();
      }
    } catch (err: any) {
      console.error('Error deleting machine:', err);
      toast.error(err.response?.data?.error || 'Không thể xóa máy');
    } finally {
      setDeleting(false);
    }
  };

  const handleUpdateMachineName = async () => {
    if (!editingMachine || !newMachineName.trim()) {
      toast.error('Vui lòng nhập tên máy');
      return;
    }

    try {
      setUpdating(true);
      const response = await updateMachineName(editingMachine.machineCode, newMachineName.trim());
      
      if (response.success) {
        // Cập nhật danh sách machines
        setMachines(prevMachines =>
          prevMachines.map(m =>
            m.machineCode === editingMachine.machineCode
              ? { ...m, machineName: response.machine.machineName }
              : m
          )
        );
        toast.success('Đã cập nhật tên máy thành công');
        handleCloseEditDialog();
      }
    } catch (err: any) {
      console.error('Error updating machine name:', err);
      toast.error(err.response?.data?.error || 'Không thể cập nhật tên máy');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <UserPageShell>
      <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
        {t.deviceMgmt.title}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t.deviceMgmt.subtitle}
      </Typography>

      {!readOnly && (
      <Accordion
        disableGutters
        elevation={0}
        sx={{
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          '&:before': { display: 'none' },
          overflow: 'hidden',
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <PaletteIcon color="primary" fontSize="small" />
            <Typography fontWeight={700}>{t.colorApps.titleAll}</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <AppAccentColorApps />
        </AccordionDetails>
      </Accordion>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : machines.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t.deviceMgmt.emptyTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t.deviceMgmt.emptyHint}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={{ xs: 1.25, sm: 2.5 }}>
          {[...machines]
            .sort((a, b) => Number(Boolean(b.isAppRunning)) - Number(Boolean(a.isAppRunning)))
            .map((machine, index) => (
            <Grid item xs={6} sm={6} md={4} key={machine.machineCode}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  border: '2px solid',
                  borderColor: machine.isAppRunning ? '#15803d' : '#ef4444',
                  bgcolor: machine.isAppRunning ? '#86efac' : '#fff',
                  boxShadow: machine.isAppRunning
                    ? '0 8px 22px rgba(22,163,74,0.28)'
                    : '0 8px 22px rgba(239,68,68,0.1)',
                  ...userCardEnterSx(index),
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, px: { xs: 1.25, sm: 2 }, pt: { xs: 1.25, sm: 2 }, pb: { xs: 1, sm: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, mb: 0.75 }}>
                    {machine.appPlatform === 'android' ? (
                      <PhoneAndroidIcon sx={{ fontSize: { xs: 22, sm: 28 }, color: 'primary.main', flexShrink: 0 }} />
                    ) : (
                      <ComputerIcon sx={{ fontSize: { xs: 22, sm: 28 }, color: 'primary.main', flexShrink: 0 }} />
                    )}
                    <Typography
                      component="h2"
                      fontWeight="bold"
                      noWrap
                      title={machine.machineName || machine.machineCode}
                      sx={{ minWidth: 0, flex: 1, fontSize: { xs: '0.95rem', sm: '1.15rem' } }}
                    >
                      {machine.machineName || machine.machineCode}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, wordBreak: 'break-word' }}>
                    {t.deviceMgmt.machineCode} <strong>{machine.machineCode}</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, minWidth: 0 }}>
                      {machine.appProduct && (
                        <Chip
                          label={
                            machine.appProduct === 'mono'
                              ? t.deviceMgmt.productMono
                              : machine.appProduct === 'pro'
                                ? t.deviceMgmt.productPro
                                : t.deviceMgmt.productLite
                          }
                          size="small"
                          {...getAppProductChipProps(machine.appProduct)}
                        />
                      )}
                    </Box>
                    {!readOnly && (
                      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleEditClick(machine, e)}
                          sx={{ color: 'primary.main', width: 28, height: 28 }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => handleDeleteClick(machine, e)}
                          sx={{ color: 'error.main', width: 28, height: 28 }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => handleMachineClick(machine.machineCode)}
                  >
                    {t.deviceMgmt.viewAccess}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog đổi tên máy */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t.deviceMgmt.renameTitle}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t.deviceMgmt.machineCode} <strong>{editingMachine?.machineCode}</strong>
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label={t.deviceMgmt.nameLabel}
              fullWidth
              variant="outlined"
              value={newMachineName}
              onChange={(e) => setNewMachineName(e.target.value)}
              placeholder={t.deviceMgmt.namePlaceholder}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !updating) {
                  handleUpdateMachineName();
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={updating}>
            {t.common.cancel}
          </Button>
          <Button
            onClick={handleUpdateMachineName}
            variant="contained"
            disabled={updating || !newMachineName.trim()}
          >
            {updating ? t.common.updating : t.common.update}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog xóa máy */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{t.deviceMgmt.deleteTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {formatMessage(t.deviceMgmt.deleteConfirm, {
              code: deletingMachine?.machineName || deletingMachine?.machineCode || '',
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleting}>
            {t.common.cancel}
          </Button>
          <Button
            onClick={handleDeleteMachine}
            variant="contained"
            color="error"
            disabled={deleting}
          >
            {deleting ? t.common.updating : t.common.delete}
          </Button>
        </DialogActions>
      </Dialog>
    </UserPageShell>
  );
};

export default MobileUploadMachineList;

